import { useCallback, useMemo } from 'react';
import dayjs from 'dayjs';
import isBetween from 'dayjs/plugin/isBetween';
import isoWeek from 'dayjs/plugin/isoWeek';
import { V1 as AiSettingsV1 } from '@frontend/api-ai-settings';
import { useAppScopeStore } from '@frontend/scope';
import { useFormField } from '@frontend/design-system';
import { getMaxCalendarDate } from '../../../utils';
import { getWeekRange } from '../../../utils/date-helpers';
import {
  AvailabilityFilters,
  AvailabilityHeaderFieldProps,
  TIME_RANGES,
  UseAvailabilityFiltersArgs,
} from './availability-header.types';

dayjs.extend(isoWeek);
dayjs.extend(isBetween);

export const useAvailabilityFilters = ({ locationId, orgId, initialServiceId }: UseAvailabilityFiltersArgs) => {
  const { selectedLocationIds, getScopeName, isSingleTypeScope } = useAppScopeStore();
  const isMultiLocation = !isSingleTypeScope;

  const { data: appointmentTypesData } = AiSettingsV1.Queries.useGetSchedulingAppointmentTypesQuery({
    request: { locationId, orgId },
    options: { enabled: !!locationId && !!orgId },
  });

  const appointmentTypes = appointmentTypesData?.appointmentTypes ?? [];
  const serviceSelectorField = useFormField({ type: 'text', value: initialServiceId ?? '' });
  const selectedServiceId = serviceSelectorField.value || appointmentTypes[0]?.id || '';

  const onServiceSelect = useCallback(
    (id: string) => serviceSelectorField.onChange({ value: id, name: 'serviceId' }),
    [serviceSelectorField]
  );

  const { todayFormatted, todayWeekStart } = useMemo(
    () => ({
      todayFormatted: dayjs().format('MM/DD/YYYY'),
      todayWeekStart: dayjs().day(0).startOf('day').format('YYYY-MM-DD'),
    }),
    []
  );

  const weekDatePicker = useFormField(
    { type: 'datePicker', value: todayFormatted, required: true, maxDate: getMaxCalendarDate() },
    []
  );

  const { weekStartDate, weekEndDate, isTodayWeek } = useMemo(() => {
    const { startOfWeek, endOfWeek } = getWeekRange(weekDatePicker.value);
    const start = startOfWeek.format('YYYY-MM-DD');
    return {
      weekStartDate: start,
      weekEndDate: endOfWeek.format('YYYY-MM-DD'),
      isTodayWeek: start === todayWeekStart,
    };
  }, [weekDatePicker.value, todayWeekStart]);

  const goToToday = () => weekDatePicker.onChange({ name: 'weekDatePicker', value: dayjs().format('MM/DD/YYYY') });

  const { data: resourcesData } = AiSettingsV1.Queries.useGetSchedulingResourcesQuery({
    request: { locationId, orgId },
    options: { enabled: !!locationId && !!orgId },
  });

  const operatories = useMemo(
    () =>
      (resourcesData?.resources ?? []).map((operatory) => ({
        value: operatory.id ?? '',
        label: operatory.name ?? operatory.id ?? '',
      })),
    [resourcesData?.resources]
  );
  const operatoryChecklistField = useFormField({ type: 'checklist', value: [] });

  const { data: staffsData } = AiSettingsV1.Queries.useGetSchedulingStaffsQuery({
    request: { locationId, orgId },
    options: { enabled: !!locationId && !!orgId },
  });
  const providers = useMemo(
    () =>
      (staffsData?.staffs ?? []).map((provider) => ({
        value: provider.id ?? '',
        label: provider.name ?? provider.id ?? '',
      })),
    [staffsData?.staffs]
  );
  const providerChecklistField = useFormField({ type: 'checklist', value: [] });

  const locationOptions = useMemo(
    () => selectedLocationIds.map((id) => ({ value: id, label: getScopeName(id) })),
    [selectedLocationIds, getScopeName]
  );
  const locationChecklistField = useFormField({ type: 'checklist', value: [] });

  const timeRangeOptions = useMemo(
    () => TIME_RANGES.map((timeRange) => ({ value: timeRange.id, label: timeRange.label })),
    []
  );
  const timeRangeChecklistField = useFormField({ type: 'checklist', value: [] });

  const resolvedFilters: AvailabilityFilters = useMemo(() => {
    const resolvedOperatoryIds =
      operatoryChecklistField.value.length > 0 ? operatoryChecklistField.value : operatories.map((o) => o.value);
    const resolvedProviderIds =
      providerChecklistField.value.length > 0 ? providerChecklistField.value : providers.map((p) => p.value);
    const resolvedLocationIds = locationChecklistField.value.length > 0 ? locationChecklistField.value : [locationId];
    const resolvedTimeRanges =
      timeRangeChecklistField.value.length > 0
        ? TIME_RANGES.filter((timeRange) => timeRangeChecklistField.value.includes(timeRange.id))
        : TIME_RANGES;
    return {
      serviceId: selectedServiceId,
      locationIds: resolvedLocationIds,
      timeRanges: resolvedTimeRanges,
      operatoryIds: resolvedOperatoryIds,
      providerIds: resolvedProviderIds,
      weekStartDate,
      weekEndDate,
    };
  }, [
    selectedServiceId,
    operatoryChecklistField.value,
    providerChecklistField.value,
    locationChecklistField.value,
    timeRangeChecklistField.value,
    weekStartDate,
    weekEndDate,
    locationId,
    operatories,
    providers,
  ]);

  const headerFieldProps: AvailabilityHeaderFieldProps = {
    weekInView: weekDatePicker.value,
    weekDatePicker,
    handleDateChange: weekDatePicker.onChange,
    goToToday,
    isTodayWeek,
    selectedServiceId: selectedServiceId || undefined,
    onServiceSelect,
    isMultiLocation,
    locationOptions,
    locationChecklistField,
    timeRangeOptions,
    timeRangeChecklistField,
    operatories,
    operatoryChecklistField,
    providers,
    providerChecklistField,
  };

  return { resolvedFilters, headerFieldProps };
};
